import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CalendarDays, Clock } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { HeroBackground } from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";
import { archivePosts, featuredPosts, type BlogPost } from "@/data/blogPosts";
import { seoHead } from "@/components/SEOHead";
import blogHero from "@/assets/mckay/roofing-service.webp.asset.json";

export const Route = createFileRoute("/blog/")({
  head: () =>
    seoHead({
      title: "Southern California Roofing Blog | McKay",
      description:
        "Roof repair advice, real project write-ups, solar cost breakdowns and help for homeowners left behind by closed contractors, from a crew on local roofs since 1981.",
      path: "/blog",
      keywords:
        "Southern California roofing blog, roof repair tips, roof replacement projects, solar cost San Diego, closed solar company help",
    }),
  component: BlogIndexPage,
});

function formatDate(iso: string) {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

function PostCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition duration-300 hover:-translate-y-1 hover:border-accent hover:shadow-xl"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden">
        <img
          src={post.hero.src}
          alt={post.hero.alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
        />
        <span className="absolute left-4 top-4 rounded-full bg-primary/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground backdrop-blur">
          {post.category}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
            {formatDate(post.datePublished)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {post.readMinutes} min read
          </span>
        </div>
        <h3 className="mt-3 font-display text-lg font-bold text-balance text-primary transition group-hover:text-accent">
          {post.h1}
        </h3>
        <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-muted-foreground">{post.metaDescription}</p>
        <span className="mt-4 text-sm font-bold text-accent">Read the post</span>
      </div>
    </Link>
  );
}

function BlogIndexPage() {
  const sorted = [...featuredPosts].sort((a, b) => b.datePublished.localeCompare(a.datePublished));
  const groups = ["Projects", "Roofing Insights", "Solar Insights", "Solar Service"].filter((c) =>
    sorted.some((p) => p.category === c),
  );
  const other = sorted.filter((p) => !groups.includes(p.category));

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={blogHero.url} alt="McKay crew installing a new roof on a Southern California home" />
          <div className="relative mx-auto flex min-h-[70vh] max-w-5xl flex-col justify-center px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-28">
            <h1 className="font-display text-4xl font-extrabold sm:text-5xl lg:text-6xl text-balance">
              Roofing &amp; solar advice from Southern California roofs
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">
              Straight answers on roof repair and replacement, real project write-ups, solar and battery costs, and what
              to do when the company that installed your system is no longer in business.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="btn-lift btn-shine">
                <Link to="/contact">Get My Free Estimate</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="btn-shine border-white/50 bg-white/10 text-white hover:bg-white hover:text-primary"
              >
                <Link to="/blog/archive">More Projects ({archivePosts.length})</Link>
              </Button>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl space-y-16 px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          {groups.map((category) => (
            <section key={category} id={category.toLowerCase().replace(/\s+/g, "-")}>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent">{category}</span>
              <h2 className="mt-2 font-display text-3xl font-extrabold text-primary sm:text-4xl">
                {category === "Projects" ? "Recent work" : category}
              </h2>
              <div className="mt-10 grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {sorted
                  .filter((p) => p.category === category)
                  .map((p) => (
                    <PostCard key={p.slug} post={p} />
                  ))}
              </div>

              {category === "Projects" && (
                <section className="relative mt-16 overflow-hidden rounded-3xl px-6 py-14 text-center sm:px-10 sm:py-18">
                  <div className="relative mx-auto max-w-2xl">
                    <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent">Explore our full library</p>
                    <h2 className="mt-3 font-display text-3xl font-extrabold text-primary sm:text-4xl">
                      See more McKay roofing &amp; solar projects
                    </h2>
                    <p className="mt-4 text-primary">
                      Browse our complete archive of real project write-ups, repair case studies, and solar installs
                      across Southern California.
                    </p>
                    <Button
                      asChild
                      size="lg"
                      variant="outline"
                      className="btn-lift mt-8 px-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    >
                      <Link to="/blog/archive">
                        View more projects
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    </Button>
                  </div>
                </section>
              )}
            </section>
          ))}

          {other.length > 0 && (
            <section>
              <h2 className="font-display text-3xl font-extrabold text-primary sm:text-4xl">More reading</h2>
              <div className="mt-10 grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {other.map((p) => (
                  <PostCard key={p.slug} post={p} />
                ))}
              </div>
            </section>
          )}
        </div>

        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}
